/**
 * DAGNode — Custom ReactFlow node for pipeline steps on the agent canvas.
 *
 * Visual spec (220px wide, sharper corners):
 *   - Left accent border: 4px colored by step type
 *   - Review badge: top-right circle (pass=green, fixed=blue, warning=amber)
 *   - Node icon: colored 28x28 square
 *   - Title + status dot + description + metadata (row count, col count)
 *   - Progress bar when status=running
 *   - Floating action toolbar above node when selected
 *   - ReactFlow Handles: top (target) + bottom (source)
 */
import React, { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import type { NodeProps } from 'reactflow';
import { Handle, Position } from 'reactflow';
import { formatCheckSummaryLine } from './reviewSummaryUtils';
import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircleOutline as PassIcon,
  AutoFixHigh as FixIcon,
  WarningAmber as WarnIcon,
  Visibility as InspectIcon,
  VisibilityOff as HideIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  TableChart as PreviewIcon,
  ViewColumn as ColumnIcon,
  TableRows as RowsIcon,
} from '@mui/icons-material';
import { getNodeColor, getNodeIcon } from './nodeConstants';

// ── Types ────────────────────────────────────────────────────────────

export interface SchemaWarning {
  operationId: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  brokenColumns?: string[];
}

export interface DAGNodeData {
  stepId: string;
  stepType: string;
  title: string;
  /** Short user-facing operation-type label rendered as a small kind badge. */
  kind?: string;
  description?: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'awaiting_review';
  rowCount?: number;
  columnCount?: number;
  cost?: number;
  reviewResult?: 'pass' | 'fixed' | 'warning' | null;
  progress?: number;
  inputRefs: string[];
  outputRef?: string;
  operationStatus?: string;
  exploratory?: boolean;
  codeSnippet?: string;
  reviewChecks?: Array<{ name: string; passed: boolean; measured?: number; threshold?: number }>;
  reviewCorrections?: Array<{ check_name: string; fix_type: string; description: string; succeeded: boolean }>;
  onDiscard?: (stepId: string) => void;
  onMarkExploratory?: (stepId: string, exploratory: boolean) => void;
  onRename?: (stepId: string, label: string) => void;
  /** Schema evolution warning — set when source replacement breaks this node. */
  schemaWarning?: SchemaWarning;
}

// ── Color + icon maps ────────────────────────────────────────────────

const REVIEW_BADGE: Record<string, { icon: React.ReactElement; color: string }> = {
  pass: { icon: <PassIcon sx={{ fontSize: 12 }} />, color: '#22c55e' },
  fixed: { icon: <FixIcon sx={{ fontSize: 12 }} />, color: '#3b82f6' },
  warning: { icon: <WarnIcon sx={{ fontSize: 12 }} />, color: '#f59e0b' },
};

// ── Component ────────────────────────────────────────────────────────

const DAGNode: React.FC<NodeProps<DAGNodeData>> = ({ data, selected, sourcePosition, targetPosition }) => {
  const targetHandlePos = targetPosition ?? Position.Top;
  const sourceHandlePos = sourcePosition ?? Position.Bottom;
  const theme = useTheme();
  const [hovered, setHovered] = React.useState(false);
  const color = getNodeColor(data.stepType);
  const icon = getNodeIcon(data.stepType, 18);
  const badge = data.reviewResult ? REVIEW_BADGE[data.reviewResult] : null;
  const schemaWarning = data.schemaWarning;

  const isPending = data.status === 'pending';
  const isRunning = data.status === 'running';
  const isFailed = data.status === 'failed';
  const isCompleted = data.status === 'completed';
  const isExploratory = data.exploratory === true;

  // Quality check summary (memoized)
  const checkSummary = useMemo(
    () => formatCheckSummaryLine(data.reviewChecks, data.reviewCorrections),
    [data.reviewChecks, data.reviewCorrections],
  );

  // Summary background color: green = all pass, blue = has fixes, amber = has failures
  const summaryBg = useMemo(() => {
    if (!data.reviewChecks?.length) return undefined;
    const hasFailures = data.reviewChecks.some(
      (ch) => !ch.passed && !(data.reviewCorrections ?? []).some((c) => c.check_name === ch.name && c.succeeded),
    );
    if (hasFailures) return alpha('#f59e0b', 0.08);
    const hasFixed = (data.reviewCorrections ?? []).some((c) => c.succeeded);
    if (hasFixed) return alpha('#3b82f6', 0.08);
    return alpha('#22c55e', 0.08);
  }, [data.reviewChecks, data.reviewCorrections]);

  // Floating toolbar: shown on hover or selection (not while running)
  const showToolbar = (hovered || selected) && !isRunning;
  // Allow delete for executed operations and AI-generated ones with 'promoted'/'corrected' status
  const canDelete = (data.operationStatus === 'executed' || data.operationStatus === 'promoted' || data.operationStatus === 'corrected') && !!data.onDiscard;
  const canToggleExploratory = (data.operationStatus === 'executed' || data.operationStatus === 'promoted' || data.operationStatus === 'corrected') && !!data.onMarkExploratory;
  const canRename = !!data.onRename;

  // Inline rename state
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startRename = useCallback(() => {
    setEditValue(data.title || data.stepType.replace(/_/g, ' '));
    setEditing(true);
  }, [data.title, data.stepType]);

  const commitRename = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== data.title && data.onRename) {
      data.onRename(data.stepId, trimmed);
    }
    setEditing(false);
  }, [editValue, data]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Status dot color
  const statusDotColor = isFailed
    ? theme.palette.error.main
    : isCompleted
      ? '#22c55e'
      : isRunning
        ? theme.palette.primary.main
        : theme.palette.grey[400];

  return (
    <Box
      sx={{ position: 'relative', pt: '40px', mt: '-40px' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* ── Floating toolbar (above node, on hover or selection) ── */}
      {showToolbar && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1px solid',
            borderColor: 'divider',
            zIndex: 10,
          }}
        >
          <Tooltip title="Inspect" placement="top" arrow>
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InspectIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>

          {data.outputRef && (
            <Tooltip title="Data preview" placement="top" arrow>
              <IconButton size="small" sx={{ p: 0.5 }}>
                <PreviewIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Rename button hidden — revisit in Phase 4 */}
          {false && canRename && (
            <Tooltip title="Rename" placement="top" arrow>
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  startRename();
                }}
              >
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}

          {canToggleExploratory && (
            <Tooltip title={isExploratory ? 'Show in pipeline' : 'Hide from pipeline'} placement="top" arrow>
              <IconButton
                size="small"
                sx={{ p: 0.5, color: isExploratory ? 'info.main' : 'text.secondary' }}
                onClick={(e) => {
                  e.stopPropagation();
                  data.onMarkExploratory!(data.stepId, !isExploratory);
                }}
              >
                {isExploratory ? <InspectIcon sx={{ fontSize: 15 }} /> : <HideIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          )}

          {canDelete && (
            <Tooltip title="Delete" placement="top" arrow>
              <IconButton
                size="small"
                sx={{ p: 0.5, color: 'error.main' }}
                onClick={(e) => {
                  e.stopPropagation();
                  data.onDiscard!(data.stepId);
                }}
              >
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* ── Node card ────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          width: 220,
          borderRadius: 0.5,
          bgcolor: 'background.paper',
          border: '2px solid',
          borderColor: schemaWarning?.severity === 'error'
            ? '#ef4444'
            : schemaWarning?.severity === 'warning'
              ? '#f59e0b'
              : selected
                ? theme.palette.primary.main
                : isFailed
                  ? theme.palette.error.main
                  : isRunning
                    ? theme.palette.primary.main
                    : 'divider',
          borderStyle: schemaWarning?.severity === 'warning'
            ? 'dashed'
            : isPending || isExploratory
              ? 'dashed'
              : 'solid',
          borderLeft: `4px solid ${isExploratory ? alpha(color, 0.5) : color}`,
          opacity: isPending ? 0.6 : isExploratory ? 0.65 : 1,
          boxShadow: schemaWarning?.severity === 'error'
            ? `0 0 0 3px ${alpha('#ef4444', 0.2)}`
            : selected
              ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
              : isRunning
                ? `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`
                : '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'border-color 0.2s, box-shadow 0.2s, opacity 0.2s',
          cursor: 'pointer',
          ...(schemaWarning?.severity === 'error' && {
            animation: 'schemaPulse 2s ease-in-out infinite',
            '@keyframes schemaPulse': {
              '0%, 100%': { boxShadow: `0 0 0 3px ${alpha('#ef4444', 0.2)}` },
              '50%': { boxShadow: `0 0 0 5px ${alpha('#ef4444', 0.35)}` },
            },
          }),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
        }}
      >
        {/* Target handle (top in TB layout, left in LR) */}
        <Handle
          type="target"
          position={targetHandlePos}
          style={{
            width: 8,
            height: 8,
            background: color,
            border: '2px solid #fff',
            top: -5,
          }}
        />

        {/* Schema warning badge — takes priority over review badge */}
        {schemaWarning ? (
          <Tooltip title={schemaWarning.message} placement="top" arrow>
            <Box
              sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: schemaWarning.severity === 'error' ? '#ef4444' : '#f59e0b',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                border: '2px solid #fff',
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              !
            </Box>
          </Tooltip>
        ) : badge ? (
          /* Review badge */
          <Box
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: badge.color,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              border: '2px solid #fff',
            }}
          >
            {badge.icon}
          </Box>
        ) : null}

        {/* Body */}
        <Box sx={{ p: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            {/* Icon square */}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 0.75,
                bgcolor: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>

            {/* Title + description */}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {editing ? (
                  <input
                    ref={inputRef}
                    value={editValue}
                    maxLength={120}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') commitRename();
                      if (e.key === 'Escape') setEditing(false);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      lineHeight: 1.3,
                      flex: 1,
                      minWidth: 0,
                      border: '1px solid',
                      borderColor: theme.palette.primary.main,
                      borderRadius: 4,
                      padding: '1px 4px',
                      outline: 'none',
                      background: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    }}
                  />
                ) : (
                  <Tooltip title={data.title || data.stepType.replace(/_/g, ' ')} placement="top" arrow disableInteractive>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{
                        fontSize: 12,
                        lineHeight: 1.3,
                        flex: 1,
                        cursor: 'default',
                      }}
                      onDoubleClick={undefined}
                    >
                      {data.title || data.stepType.replace(/_/g, ' ')}
                    </Typography>
                  </Tooltip>
                )}
                {/* Status dot */}
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: statusDotColor,
                    flexShrink: 0,
                    animation: isRunning ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                  }}
                />
              </Stack>

              {/* Output table name — identity the user references in
                  queries and downstream nodes. Monospaced with a `→`
                  prefix to signal "produces"; quiet styling so it
                  doesn't compete with the label above. Hidden when
                  the title already IS the output_ref (i.e. the
                  fallback chain landed there because label was
                  missing). */}
              {data.outputRef && data.outputRef !== data.title && (
                <Tooltip title={data.outputRef} placement="bottom" arrow disableInteractive>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{
                      fontSize: 10,
                      lineHeight: 1.3,
                      display: 'block',
                      mt: 0.25,
                      fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                    }}
                  >
                    → {data.outputRef}
                  </Typography>
                </Tooltip>
              )}

              {/* Description snippet */}
              {data.description && (
                <Tooltip title={data.description} placement="bottom" arrow disableInteractive>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: 10, lineHeight: 1.3, display: 'block', mt: 0.25 }}
                  >
                    {data.description}
                  </Typography>
                </Tooltip>
              )}

              {/* Code snippet */}
              {data.codeSnippet && (
                <Tooltip
                  title={data.codeSnippet}
                  placement="bottom"
                  arrow
                  disableInteractive
                  componentsProps={{
                    tooltip: {
                      sx: { maxWidth: 500, fontFamily: '"Fira Code", monospace', fontSize: 11, whiteSpace: 'pre-wrap' },
                    },
                  }}
                >
                  <Box
                    sx={{
                      mt: 0.5,
                      px: 0.75,
                      py: 0.5,
                      bgcolor: alpha('#1e293b', 0.06),
                      borderRadius: 0.5,
                      overflow: 'hidden',
                    }}
                  >
                    <Typography
                      component="code"
                      sx={{
                        fontFamily: '"Fira Code", monospace',
                        fontSize: 9,
                        lineHeight: 1.3,
                        color: 'text.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-all',
                      }}
                    >
                      {data.codeSnippet}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Stack>

          {/* Metadata row */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 0.75, justifyContent: 'flex-start', alignItems: 'center' }}
          >
            {data.rowCount != null && (
              <Stack direction="row" spacing={0.25} alignItems="center">
                <RowsIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {data.rowCount.toLocaleString()}
                </Typography>
              </Stack>
            )}
            {data.columnCount != null && (
              <Stack direction="row" spacing={0.25} alignItems="center">
                <ColumnIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {data.columnCount}
                </Typography>
              </Stack>
            )}
            {data.cost != null && data.cost > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, ml: 'auto !important' }}>
                ${data.cost.toFixed(4)}
              </Typography>
            )}
            {/* Kind tag — subtle bottom-right marker of the operation type.
                The colored top-left icon already conveys this at a glance;
                the text tag is here for users who want the explicit name. */}
            {data.kind && (
              <Tooltip title={data.stepType.replace(/_/g, ' ')} placement="top" arrow disableInteractive>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: alpha(color, 0.85),
                    // Pin to the right edge unless cost already claimed it.
                    ml: data.cost != null && data.cost > 0 ? 0.5 : 'auto !important',
                  }}
                >
                  {data.kind}
                </Typography>
              </Tooltip>
            )}
          </Stack>

          {/* Quality check summary strip */}
          {isCompleted && checkSummary && (
            <Tooltip title={checkSummary} placement="bottom" arrow disableInteractive>
              <Box
                sx={{
                  mt: 0.5,
                  px: 0.75,
                  py: 0.25,
                  bgcolor: summaryBg,
                  borderRadius: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontSize: 9,
                    lineHeight: 1.3,
                    color: 'text.secondary',
                    display: 'block',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {checkSummary}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Progress bar */}
        {isRunning && (
          <LinearProgress
            variant={data.progress != null ? 'determinate' : 'indeterminate'}
            value={data.progress}
            sx={{ height: 3 }}
          />
        )}

        {/* Source handle (bottom in TB layout, right in LR) */}
        <Handle
          type="source"
          position={sourceHandlePos}
          style={{
            width: 8,
            height: 8,
            background: color,
            border: '2px solid #fff',
            bottom: -5,
          }}
        />
      </Box>
    </Box>
  );
};

export default memo(DAGNode);
